import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, DatePicker, Row, Spin, Statistic, Typography } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";
import AgenceListingsModal from "./AgenceListingsModal";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

/**
 * Onglet « Suivi agence » : chiffre d'affaires, commissions (générées/payées/dues) et
 * primes reversées / non reversées en compagnie, sur une période. Chaque carte ouvre la
 * modale de listings détaillés (mêmes états que /reseau/suivi-agences, exportables Excel/PDF).
 * Source : GET /api/agence/{codeAgence}/suivi?start&end&codeOrganisme
 */
const SuiviAgenceTab = ({ codeAgence, codeOrganisme, nomAgence }) => {
    const [periode, setPeriode] = useState([dayjs().startOf("year"), dayjs()]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // Modale de listings détaillés (7 rubriques, exportables Excel/PDF).
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState("ENCAISSEES");

    const fetchSuivi = useCallback(() => {
        if (!codeAgence) return;
        setLoading(true);
        const params = {};
        if (periode?.[0]) params.start = periode[0].format("YYYY-MM-DD");
        if (periode?.[1]) params.end = periode[1].format("YYYY-MM-DD");
        if (codeOrganisme) params.codeOrganisme = codeOrganisme;
        axiosInstance
            .get(`/api/agence/${codeAgence}/suivi`, { params })
            .then((res) => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [codeAgence, codeOrganisme, periode]);

    useEffect(() => {
        fetchSuivi();
    }, [fetchSuivi]);

    const fmt = (v) => `${displayNumbers(Math.trunc(v || 0))} F`;

    // Ouvre la modale de listings sur la rubrique voulue.
    const openListings = (tab) => { setModalTab(tab); setModalOpen(true); };
    // Carte cliquable → ouvre le listing détaillé de la rubrique correspondante.
    const drillProps = (tab) => ({ hoverable: true, onClick: () => openListings(tab), style: { cursor: "pointer" } });
    const resteAEncaisser = Math.max((data?.chiffreAffaires || 0) - (data?.primesEncaissees || 0), 0);

    return (
        <div style={{ padding: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <Title level={5} style={{ margin: 0 }}>Suivi de l'agence</Title>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <RangePicker format="DD/MM/YYYY" value={periode} onChange={setPeriode} allowClear={false} />
                    <Button type="primary" icon={<UnorderedListOutlined />} onClick={() => openListings("ENCAISSEES")}>
                        États détaillés & exports
                    </Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* Vue d'ensemble — cartes cliquables : ouvrent le listing détaillé (exportable Excel/PDF). */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12} xl={6}>
                        <Card {...drillProps("CA")}><Statistic title="Production (primes TTC)" value={fmt(data?.chiffreAffaires)} /></Card>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Card {...drillProps("CA")}><Statistic title="Nombre de contrats" value={data?.nbContrats ?? 0} /></Card>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Card {...drillProps("ENCAISSEES")}><Statistic title="Primes encaissées" value={fmt(data?.primesEncaissees)} valueStyle={{ color: "#389e0d" }} /></Card>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Card {...drillProps("NON_ENCAISSEES")}><Statistic title="Primes non encaissées" value={fmt(resteAEncaisser)} valueStyle={{ color: "#d48806" }} /></Card>
                    </Col>
                </Row>
                <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                    Cliquez sur une carte pour ouvrir le listing détaillé (exportable Excel et PDF).
                </Text>

                {/* Commissions */}
                <Title level={5} style={{ marginTop: 24 }}>Commissions</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}><Card {...drillProps("COMM_DUES")}><Statistic title="Générées" value={fmt(data?.commissionsGenerees)} /></Card></Col>
                    <Col xs={24} md={8}><Card {...drillProps("COMM_ACQUISES")}><Statistic title="Payées" value={fmt(data?.commissionsPayees)} valueStyle={{ color: "#389e0d" }} /></Card></Col>
                    <Col xs={24} md={8}><Card {...drillProps("COMM_DUES")}><Statistic title="Dues" value={fmt(data?.commissionsDues)} valueStyle={{ color: "#d48806" }} /></Card></Col>
                </Row>

                {/* Reversements */}
                <Title level={5} style={{ marginTop: 24 }}>Reversement de primes</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}><Card {...drillProps("REVERSEES")}><Statistic title="Primes reversées" value={fmt(data?.primesReversees)} valueStyle={{ color: "#389e0d" }} /></Card></Col>
                    <Col xs={24} md={12}><Card {...drillProps("NON_REVERSEES")}><Statistic title="Primes non reversées (en attente)" value={fmt(data?.primesNonReversees)} valueStyle={{ color: "#d48806" }} /></Card></Col>
                </Row>

                <Text type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
                    CA &amp; commissions : sur la période (date d'effet / création) ; primes reversées : date de versement en compagnie ;
                    non reversées : bordereaux de reversement encore en attente (stock courant).
                </Text>
            </Spin>

            <AgenceListingsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                codeAgence={codeAgence}
                codeOrganisme={codeOrganisme}
                nomAgence={nomAgence}
                periode={periode}
                initialTab={modalTab}
            />
        </div>
    );
};

export default SuiviAgenceTab;
